import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Output,
    ViewChild,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { CounterpartyService } from '../../services/counterparty/counterparty.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    CounterpartyCardViewModel,
    CounterpartyCardComponent,
} from '../counterparty-card/counterparty-card.component';
import {
    each as _each,
    find as _find,
    map as _map,
    get as _get,
    trim as _trim,
    isFinite as _isFinite,
    isEmpty as _isEmpty,
    isArray as _isArray,
    parseInt as _parseInt,
    toString as _toString,
} from 'lodash';
import { ThAddressSuggestorService } from 'src/app/shared/services';
import { DxTextBoxComponent } from 'devextreme-angular';
import { DxTextBoxComponent as DxTextBoxComponent_1 } from 'devextreme-angular/ui/text-box';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxLoadIndicatorComponent } from 'devextreme-angular/ui/load-indicator';

@Component({
    selector: 'app-counterparty-finder',
    templateUrl: './counterparty-finder.component.html',
    styleUrls: ['./counterparty-finder.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxTextBoxComponent_1,
        DxButtonComponent,
        DxLoadIndicatorComponent,
        CounterpartyCardComponent,
    ],
})
export class CounterpartyFinderComponent {
    private cdref = inject(ChangeDetectorRef);
    private counterpartyService = inject(CounterpartyService);
    private thAddressSuggestor = inject(ThAddressSuggestorService);

    selectedCtp?: Counterparty;
    @Output() ctpSelectionChangeEvent = new EventEmitter<Counterparty>();

    @ViewChild('inputTextBox', { static: true })
    taxIdInput!: DxTextBoxComponent;

    inputTaxId = '';
    statusMsg = '';
    isLoaded = false;
    ctpList: Counterparty[] = [];
    ctpVms: CounterpartyCardViewModel[] = [];
    isLoading = false;
    // Companies returned by a name search, pending a pick to load their
    // branches. Empty whenever the search was by tax ID.
    companyMatches: CompanyMatch[] = [];

    private static readonly NO_RESULTS = 'ไม่มีข้อมูล';
    // A name search runs a headless browser and the gateway gives up at 29s,
    // so a broad query can be cut off while the scraper is still working. That
    // is not the same as "no such company", and retrying it cannot succeed.
    private static readonly SEARCH_TOO_BROAD =
        'การค้นหากว้างเกินไป กรุณาระบุชื่อบริษัทให้เฉพาะเจาะจงกว่านี้';

    // What the empty result panel says — see the two constants above.
    emptyMessage = CounterpartyFinderComponent.NO_RESULTS;

    // One box, two searches: an all-digit input is a tax ID, anything else is
    // a company name.
    onSearchClick(): void {
        const input: string = _trim(this.inputTaxId);
        if (!input) {
            return;
        }
        this.companyMatches = [];
        if (CounterpartyFinderComponent.looksLikeTaxId(input)) {
            // CounterpartyService normalises the id before it goes out.
            this.findCtp(input);
        } else {
            this.findCompaniesByName(input);
        }
    }

    // A name match carries only a tax ID and a name — no address or branches —
    // so look the company up by tax ID to get selectable records.
    onCompanyMatchClick(match: CompanyMatch): void {
        this.companyMatches = [];
        this.inputTaxId = match.taxId;
        this.findCtp(match.taxId);
    }

    onSelectButtonsClicked(ctpVm: CounterpartyCardViewModel): void {
        const selectedCtp = _find<Counterparty>(this.ctpList, { id: ctpVm.id });
        if (selectedCtp) {
            this.selectedCtp = selectedCtp;
            this.ctpSelectionChangeEvent.emit(selectedCtp);
        }
    }

    // For a host that reveals the finder on demand (a popup): puts the caret
    // in the search box so the operator can type straight away.
    focusInput(): void {
        this.taxIdInput?.instance.focus();
    }

    // Collapse separators only when the input is a tax ID — a company name has
    // to survive being typed.
    normalizeTaxIdInput(): void {
        if (!this.taxIdInput) {
            return;
        }
        const input: string = _trim(this.inputTaxId);
        this.taxIdInput.value = CounterpartyFinderComponent.looksLikeTaxId(
            input,
        )
            ? CounterpartyService.toTaxId(input)
            : input;
    }

    // Digits, possibly typed with spaces or dashes.
    private static looksLikeTaxId(input: string): boolean {
        return /^[0-9\s-]+$/.test(input);
    }

    // 504 is API Gateway abandoning the scraper at its 29s integration
    // timeout; 0 is the browser giving up on the request itself.
    private static isTimeout(error: unknown): boolean {
        const status = _get(error, 'status');
        return status === 504 || status === 0;
    }

    private findCompaniesByName(companyName: string): void {
        this.isLoading = true;
        this.isLoaded = false;
        this.ctpList = [];
        this.ctpVms = [];
        this.emptyMessage = CounterpartyFinderComponent.NO_RESULTS;
        this.statusMsg = 'กำลังค้นหาบริษัทจากชื่อ ...';
        this.counterpartyService
            .searchCompaniesByName(companyName)
            .pipe(
                // "No companies found" is a 404, which rxjs raises as an
                // error. A timeout is a different answer: the scraper was
                // still working when the gateway gave up, so say the query was
                // too broad rather than report it as no match.
                catchError((error) => {
                    if (CounterpartyFinderComponent.isTimeout(error)) {
                        this.emptyMessage =
                            CounterpartyFinderComponent.SEARCH_TOO_BROAD;
                    }
                    return of(null);
                }),
            )
            .subscribe((result) => {
                this.companyMatches =
                    CounterpartyFinderComponent.toCompanyMatches(result);
                this.isLoading = false;
                this.isLoaded = true;
                this.cdref.detectChanges();
            });
    }

    // The records are companies rather than branches, and the API doesn't
    // guarantee a tax ID appears only once, so de-duplicate before showing them.
    private static toCompanyMatches(result: unknown): CompanyMatch[] {
        const records =
            CounterpartyFinderComponent.unwrapScraperRecords(result);
        const byTaxId = new Map<string, CompanyMatch>();
        _each(records, (record: unknown) => {
            const taxId = _trim(_toString(_get(record, 'taxId')));
            const name = _trim(_toString(_get(record, 'name')));
            if (taxId && name && !byTaxId.has(taxId)) {
                byTaxId.set(taxId, { taxId, name });
            }
        });
        return Array.from(byTaxId.values());
    }

    private findCtp(taxId: string): void {
        this.isLoading = true;
        this.ctpList = [];
        this.emptyMessage = CounterpartyFinderComponent.NO_RESULTS;
        this.findCtpFromDataForThai(taxId).subscribe(
            () => {
                this.constructCardViewModels();
                this.isLoading = false;
                this.isLoaded = true;
                this.cdref.detectChanges();
            },
            () => {
                this.isLoading = false;
                this.isLoaded = true;
            },
        );
    }

    // The scraper wraps its records one level deeper than it looks:
    // { success, data: { data: [...] } }.
    private static unwrapScraperRecords(result: unknown): any[] {
        if (!_get(result, 'success', false)) {
            return [];
        }
        const records = _get(result, 'data.data', []);
        return _isArray(records) ? records : [];
    }

    private findCtpFromDataForThai(taxId: string): Observable<any> {
        this.statusMsg = 'กำลังค้นหาคู่ค้าจาก dataforthai ...';
        return this.counterpartyService.getDataForThaiCompanyData(taxId).pipe(
            // "Tax ID not found" comes back as a 404, which rxjs treats as an
            // error — without this, an unknown tax ID would abort the lookup
            // instead of falling through to the DBD API below, which may well
            // know the company (the two are backed by different registries).
            catchError(() => of(null)),
            switchMap((dbdResult) => {
                const resCtpList =
                    CounterpartyFinderComponent.unwrapScraperRecords(dbdResult);
                if (!_isEmpty(resCtpList)) {
                    for (const ctp of resCtpList) {
                        const branchId = _get(ctp, 'branchNo', 0)
                            .toString()
                            .padStart(5, '0');
                        const _taxId: string = _get(ctp, 'taxId');
                        const companyName: string = _get(ctp, 'name');
                        const address: string = _get(ctp, 'address');
                        if (_taxId && companyName && address) {
                            this.ctpList.push({
                                id: `${_taxId}|${branchId}`,
                                companyName,
                                taxId: _taxId,
                                branchId,
                                branchDisplayText:
                                    this.getBranchDisplayTextFromId(branchId),
                                address,
                                addrSegment: this.getAddrSegment(address),
                            });
                        }
                    }
                    return of([]);
                }
                this.statusMsg = 'กำลังค้นหาคู่ค้าจาก DBD API ...';
                return this.counterpartyService
                    .getDbdCounterpartyDataAlt(taxId)
                    .pipe(
                        map((res) => {
                            return this.extractDbdSelectableCtps(res);
                        }),
                    );
            }),
        );
    }

    private extractDbdSelectableCtps(dbdResults: any): void {
        _each(dbdResults, (result) => {
            const person = _get(result, 'cd:OrganizationJuristicPerson');
            const companyName = _get(person, 'cd:OrganizationJuristicNameTH');
            const taxId = _get(person, 'cd:OrganizationJuristicID');
            const branchDisplayText = _get(
                person,
                'cd:OrganizationJuristicBranchName',
            );
            const address: string = this.constructAddrFromDbdData(person);
            const branchId = this.getBranchIdFromDisplayText(branchDisplayText);
            this.ctpList.push({
                id: `${taxId}|${branchId}`,
                companyName,
                taxId,
                branchId,
                branchDisplayText,
                address,
                addrSegment: this.getAddrSegment(address),
            });
        });
    }

    private constructCardViewModels(): void {
        this.ctpVms = _map<Counterparty, CounterpartyCardViewModel>(
            this.ctpList,
            (ctp: Counterparty) => {
                return {
                    id: `${ctp.taxId}|${ctp.branchId}`,
                    companyName: ctp.companyName,
                    taxId: ctp.taxId,
                    branch: ctp.branchDisplayText,
                    address: ctp.address,
                };
            },
        );
    }

    private getBranchDisplayTextFromId(id: any): string {
        const idInt: number = _parseInt(id);
        if (_isFinite(idInt)) {
            if (idInt === 0) {
                return 'สำนักงานใหญ่';
            } else {
                return `สาขาที่ ${idInt.toString().padStart(5, '0')}`;
            }
        }
        return '';
    }

    // Inverse of getBranchDisplayTextFromId: pulls the branch number back out
    // of the DBD's branch name, e.g. "สาขาที่ 00012" -> "00012".
    private getBranchIdFromDisplayText(input: string): string {
        const dspTxt: string = _trim(input);
        if (_isEmpty(dspTxt)) {
            return '';
        }
        if (dspTxt === 'สำนักงานใหญ่') {
            return '00000';
        }
        const match = /(\d+)/.exec(dspTxt);
        const branchNo: number = match ? _parseInt(match[1]) : NaN;
        return _isFinite(branchNo) ? branchNo.toString().padStart(5, '0') : '';
    }

    private constructAddrFromDbdData(person: any): string {
        const addrType = _get(
            person,
            'cd:OrganizationJuristicAddress.cr:AddressType',
        );
        let addrLine: string = _trim(_get(addrType, 'cd:Address'));
        addrLine = addrLine.replace(/\s\s+/g, ' ');

        const province: string = _trim(
            _get(addrType, 'cd:CountrySubDivision.cr:CountrySubDivisionTextTH'),
        );
        const isBangkok: boolean = province === 'กรุงเทพมหานคร';

        let district: string = _trim(_get(addrType, 'cd:City.cr:CityTextTH'));
        if (district.startsWith('เขต')) {
            district = district.slice('เขต'.length);
        } else if (district.startsWith('อำเภอ')) {
            district = district.slice('อำเภอ'.length);
        }

        let subDistrict: string = _trim(
            _get(addrType, 'cd:CitySubDivision.cr:CitySubDivisionTextTH'),
        );
        if (subDistrict.startsWith('แขวง')) {
            subDistrict = subDistrict.slice('แขวง'.length);
        } else if (subDistrict.startsWith('ตำบล')) {
            subDistrict = subDistrict.slice('ตำบล'.length);
        }

        const postCode = _trim(
            this.thAddressSuggestor.getPostCode(
                province,
                district,
                subDistrict,
            ) ?? '',
        );

        const fullAddr = _trim(
            isBangkok
                ? `${addrLine} แขวง${subDistrict} เขต${district} จังหวัด${province} ${postCode}`
                : `${addrLine} ตำบล${subDistrict} อำเภอ${district} จังหวัด${province} ${postCode}`,
        );
        return fullAddr;
    }

    private getAddrSegment(fullAddr: string): AddressSegment {
        const postcodeRegex = /(\d{5})$/;
        const provinceRegex = /(จังหวัด|จ\.)(\S+)/;
        // The prefix is optional so both "จังหวัดกรุงเทพมหานคร" (what
        // constructAddrFromDbdData builds) and a bare "กรุงเทพมหานคร" match.
        // Without it the prefix survives every later replace and ends up
        // trailing addrLine.
        const bangkokRegex = /(จังหวัด|จ\.)?กรุงเทพมหานคร$/;
        const districtRegex = /(เขต|อำเภอ|อ\.)(\S+)/;
        const subDistrictRegex = /(แขวง|ตำบล|ต\.)(\S+)/;
        const houseNoRegex = /^[\d\-/]+/;
        const addrSeg: AddressSegment = {
            houseNo: '',
            addrLine: '',
            subDistrict: '',
            district: '',
            province: '',
            postcode: '',
        };

        let _fullAddr = fullAddr;
        const postCodeMatch: RegExpExecArray | null =
            postcodeRegex.exec(_fullAddr);
        if (postCodeMatch && postCodeMatch.length > 1) {
            const postcode: string = postCodeMatch[1];
            addrSeg.postcode = postcode;
            _fullAddr = _fullAddr.replace(postCodeMatch[0], '').trim();
        }

        const bangkokMatch: RegExpExecArray | null =
            bangkokRegex.exec(_fullAddr);
        if (bangkokMatch) {
            // bangkokMatch[0] may carry the prefix now, so name the province
            // explicitly, and cut at the match index rather than by string
            // replace: the regex is $-anchored, but replace would strip the
            // first occurrence instead.
            addrSeg.province = 'กรุงเทพมหานคร';
            _fullAddr = _fullAddr.slice(0, bangkokMatch.index).trim();
        } else {
            const provinceMatch: RegExpExecArray | null =
                provinceRegex.exec(_fullAddr);
            if (provinceMatch && provinceMatch.length > 1) {
                const province: string = provinceMatch[2];
                addrSeg.province = province;
                _fullAddr = _fullAddr.replace(provinceMatch[0], '').trim();
            } else {
                // dataforthai writes provinces bare — "... อำเภอสามพราน
                // นครปฐม 73210" — so neither regex above matches and the
                // province would survive into addrLine. Recognize a known
                // province name ending the string instead.
                const bareProvince = this.getTrailingProvince(_fullAddr);
                if (bareProvince) {
                    addrSeg.province = bareProvince;
                    _fullAddr = _fullAddr
                        .slice(0, _fullAddr.length - bareProvince.length)
                        .trim();
                }
            }
        }

        const districtMatch: RegExpExecArray | null =
            districtRegex.exec(_fullAddr);
        if (districtMatch && districtMatch.length > 1) {
            const district: string = districtMatch[2];
            addrSeg.district = district;
            _fullAddr = _fullAddr.replace(districtMatch[0], '').trim();
        }

        const subDistrictMatch: RegExpExecArray | null =
            subDistrictRegex.exec(_fullAddr);
        if (subDistrictMatch && subDistrictMatch.length > 1) {
            const subDistrict: string = subDistrictMatch[2];
            addrSeg.subDistrict = subDistrict;
            _fullAddr = _fullAddr.replace(subDistrictMatch[0], '').trim();
        }

        const houseNoMatch: RegExpExecArray | null =
            houseNoRegex.exec(_fullAddr);
        if (houseNoMatch && houseNoMatch.length > 0) {
            const houseNo: string = houseNoMatch[0];
            addrSeg.houseNo = houseNo;
            _fullAddr = _fullAddr.replace(houseNoMatch[0], '').trim();
        }
        // Removing the middle segments leaves their flanking spaces behind
        // ("สาย 5   นครปฐม"), which trim alone can't reach. The registries
        // also run Thai text into numbers ("หมู่11ถนนพุทธมณฑลสาย5"), so put
        // one space at every Thai-digit boundary, both directions. Only Thai
        // letters (ก-๎) count as text: punctuation and Latin stay
        // attached, so "33/4" and unit codes like "A1" are not split.
        addrSeg.addrLine = _fullAddr
            .replace(/([ก-๎])(?=\d)/g, '$1 ')
            .replace(/(\d)(?=[ก-๎])/g, '$1 ')
            .replace(/\s\s+/g, ' ')
            .trim();
        return addrSeg;
    }

    // The province name that ends the address as its own word, or '' if none
    // does. Longest wins so a province containing another's name as a suffix
    // can't be shadowed by the shorter match.
    private getTrailingProvince(addr: string): string {
        let longest = '';
        for (const province of this.thAddressSuggestor.getAllProvinces()) {
            if (
                province.length > longest.length &&
                (addr === province || addr.endsWith(` ${province}`))
            ) {
                longest = province;
            }
        }
        return longest;
    }
}

export interface Counterparty {
    id: string;
    companyName: string;
    taxId: string;
    branchId: string;
    branchDisplayText: string;
    address: string;
    addrSegment: AddressSegment;
}

// A company returned by a name search. Deliberately thinner than Counterparty:
// the search returns companies rather than branches, so there is no address or
// branch until the company is looked up by tax ID.
export interface CompanyMatch {
    taxId: string;
    name: string;
}

export interface AddressSegment {
    houseNo: string;
    addrLine: string;
    subDistrict: string;
    district: string;
    province: string;
    postcode: string;
}
